import { useContext, useEffect, useState } from 'react';
import { Button } from '../../../../components/form/button';
import { v4 as uuidv4 } from 'uuid';
import {
  InventoryReturnRequest,
  OffRentRequest,
  Order,
  OrderDocumentType,
  OrderProduct,
  OrderReturnStatus,
  OrderType,
  SplitOrderProductsUpdateRequest,
} from '../../../../clients/quipli-service/order/order';
import { useOrderApiV1 } from '../../../../clients/quipli-service/order/order-api-v1';
import { toast } from '../../../../util/toasts';
import { Toggle } from '../../../../components/form/toggle';
import { Dictionary, isEqual, round } from 'lodash';
import '../status-transitions.scss';
import {
  CalculateFinancialSummaryRequest,
  DamageWaiverLineItem,
  DeliveryLineItem,
  FeeLineItem,
  FinancialSummary,
  PickupLineItem,
  ProductFeeLineItem,
  ProductLineItem,
  TaxLineItem,
} from '../../../../clients/quipli-service/financials/financials';
import { toShippingType } from '../../../../clients/quipli-service/delivery/delivery';
import { useAPI } from '../../../../clients/quipli-service/quipli-service-client';
import Dinero from 'dinero.js';
import { TenantContext } from '../../../../context/tenant-context';
import {
  HourOverageOverride,
  InventoryReturnFuelChargeInput,
  InventoryReturnHourInput,
  InventoryReturnLocation,
  QuantityOffRentInput,
} from '../inventory-return';
import { Location } from '../../../../clients/quipli-service/location/location';
import { RentalProductPrice } from '../../../../clients/quipli-service/pricing/pricing';
import {
  CalculateOverageChargeResult,
  ProductTrackingType,
  ProductType,
} from '../../../../clients/quipli-service/product/product';
import { Checkbox } from '../../../../components/checkbox/checkbox';
import { Configuration } from '../../../../clients/quipli-service/configuration/configuration';
import { FuelCharge, orderProductToOrderInputItem } from './off-rent-order';
import { JobType, TaskType } from '../../../../clients/quipli-service/job-tasks/job-tasks';
import { useJobTaskApi } from '../../../../clients/quipli-service/job-tasks/job-task-api';

type PartialOffRentUnits = { unitIds: number[]; uniqueId: string };
export type PartialOffRentQuantities = { quantity: number; uniqueId: string };

export const PartialOffRentStep = ({
  order,
  setOrder,
  customPrices,
  configs,
  locations,
  offRentRequest,
  setOffRentRequest,
  inventoryReturnRequest,
  setInventoryReturnRequest,
  onCancel,
  onComplete,
  hourOverages,
  hourOverageOverrides,
  setHourOverageOverrides,
  fuelCharges,
  setFuelCharges,
}: {
  order: Order;
  setOrder: (order: Order) => void;
  customPrices?: RentalProductPrice[];
  configs: Dictionary<Configuration>;
  locations: Location[];
  offRentRequest: OffRentRequest;
  setOffRentRequest: React.Dispatch<React.SetStateAction<OffRentRequest | undefined>>;
  inventoryReturnRequest?: InventoryReturnRequest;
  setInventoryReturnRequest: React.Dispatch<React.SetStateAction<InventoryReturnRequest | undefined>>;
  onCancel: () => void;
  onComplete: () => void;
  hourOverages?: CalculateOverageChargeResult[];
  hourOverageOverrides?: HourOverageOverride[];
  setHourOverageOverrides: (
    value: React.SetStateAction<
      {
        orderProductId: number;
        unitId: number;
        overageCharge: number;
        taxable: boolean;
      }[]
    >,
  ) => void;
  fuelCharges: FuelCharge[];
  setFuelCharges: React.Dispatch<React.SetStateAction<FuelCharge[]>>;
}) => {
  const { configuration } = useContext(TenantContext);
  const { calculateFinancialSummary } = useAPI();
  const { splitOrderProductsUpdate } = useOrderApiV1();
  const { getJobByEntity } = useJobTaskApi();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [partialOffRentUnitIds, setPartialOffRentUnitIds] = useState<Dictionary<PartialOffRentUnits>>(
    Object.fromEntries(
      order.products
        .filter(
          (op) =>
            op.type === OrderType.Rental &&
            op.status === OrderReturnStatus.OnRent &&
            op.product.trackingType === ProductTrackingType.SerialUnits,
        )
        .map((op) => [op.id, { uniqueId: uuidv4(), unitIds: op.assignedUnits.map((au) => au.id) }]),
    ),
  );
  const [partialOffRentQuantities, setPartialOffRentQuantities] = useState<Dictionary<PartialOffRentQuantities>>(
    Object.fromEntries(
      order.products
        .filter(
          (op) =>
            op.type === OrderType.Rental &&
            op.status === OrderReturnStatus.OnRent &&
            op.product.trackingType === ProductTrackingType.Quantity,
        )
        .map((op) => [op.id, { uniqueId: uuidv4(), quantity: op.quantity }]),
    ),
  );
  const currency = configuration ? (configuration.currency as Dinero.Currency) : undefined;
  const unitTrackingType = configs['CONF_UNIT_TRACKING_VIA']?.value === '1' ? 'serialNumber' : 'unitNumber';
  const showInventoryReturn = configs['CONF_ALLOW_COMBINED_OFF_RENT_RETURN']?.value === '1';

  // Make sure the off rent quantity is not greater than any of the order product quantities
  const isValid =
    offRentRequest &&
    offRentRequest.offRentProducts.map((orp) => orp.productId).length > 0 &&
    offRentRequest.offRentProducts.every((orp) => {
      const op = order.products.find((op) => op.id === orp.productId);

      return orp.quantity === undefined || (op ? op.quantity >= orp.quantity : false);
    });

  useEffect(() => {
    const initFuel = async () => {
      const job = await getJobByEntity('Order', `${order.id}`, JobType.CheckinInspection);
      const newFuelCharges: FuelCharge[] = [];
      order.products.forEach((op) => {
        op?.assignedUnits.forEach((au) => {
          newFuelCharges.push({
            orderProductId: op.id,
            productId: op.product.id,
            unitId: au.id,
            amount:
              job?.tasks.find((task) => task.type === TaskType.MachineHours && task.metadata?.productUnitId === au.id)
                ?.metadata?.fuel ?? 0,
          });
        });
      });

      setFuelCharges(newFuelCharges);
    };
    initFuel();
  }, []);

  useEffect(() => {
    // toggle off rent to be off if all units unselected
    Object.entries(partialOffRentUnitIds).map(([opId, v]) => {
      if (!v.unitIds.length && offRentRequest.offRentProducts.map((orp) => orp.productId).includes(parseInt(opId))) {
        onToggleOffRent(order.products.find((op) => op.id === parseInt(opId))!);
        setPartialOffRentUnitIds({
          ...partialOffRentUnitIds,
          [opId]: {
            ...partialOffRentUnitIds[opId],
            unitIds: order.products.find((op) => op.id === parseInt(opId))?.assignedUnits.map((au) => au.id) ?? [],
          },
        });
      }
    });
  }, [partialOffRentUnitIds]);

  const isProductPartialOffRent = (opId: number): boolean => {
    return (
      offRentRequest.offRentProducts.map((orp) => orp.productId).some((id) => id === opId) &&
      partialOffRentUnitIds[opId] &&
      partialOffRentUnitIds[opId].unitIds.length > 0 &&
      !isEqual(
        partialOffRentUnitIds[opId].unitIds,
        order.products.find((op) => op.id === opId)?.assignedUnits.map((au) => au.id),
      )
    );
  };

  const isProductPartialOffRentQuantity = (opId: number): boolean => {
    return (
      offRentRequest.offRentProducts.map((orp) => orp.productId).some((id) => id === opId) &&
      partialOffRentQuantities[opId] &&
      partialOffRentQuantities[opId].quantity > 0 &&
      partialOffRentQuantities[opId].quantity !== order.products.find((op) => op.id === opId)?.quantity
    );
  };

  const next = async () => {
    try {
      setIsLoading(true);

      const anyPartialOffRentUnitOrQuantity =
        Object.keys(partialOffRentUnitIds).some((opId) => isProductPartialOffRent(parseInt(opId))) ||
        Object.keys(partialOffRentQuantities).some((opId) => isProductPartialOffRentQuantity(parseInt(opId)));

      if (order && anyPartialOffRentUnitOrQuantity) {
        const cfsReq = orderToCalculateFinancialSummaryRequest(
          order,
          customPrices ?? [],
          partialOffRentUnitIds,
          isProductPartialOffRent,
          partialOffRentQuantities,
          isProductPartialOffRentQuantity,
        );
        const updatedSummary = await calculateFinancialSummary(cfsReq);
        const partialOffRentRequest = toPartialOffRentRequest(
          order,
          customPrices ?? [],
          updatedSummary,
          offRentRequest,
          currency ?? (configs['CONF_CURRENCY']?.value as Dinero.Currency),
          partialOffRentUnitIds,
          isProductPartialOffRent,
          partialOffRentQuantities,
          isProductPartialOffRentQuantity,
        );
        const updatedOrder = await splitOrderProductsUpdate(order.id, partialOffRentRequest);
        const newSplitOrderProducts = updatedOrder.products.filter((op) => {
          return !order.products.some((ogOp) => op.id == ogOp.id);
        });
        setOffRentRequest((prev) => {
          return (
            prev && {
              ...prev,
              offRentProducts: [
                ...prev.offRentProducts.filter(
                  (orp) => !isProductPartialOffRent(orp.productId) && !isProductPartialOffRentQuantity(orp.productId),
                ),
                ...newSplitOrderProducts.map((op) => ({ productId: op.id, type: op.product.trackingType })),
              ],
            }
          );
        });

        setInventoryReturnRequest((prev) => {
          const opIds = prev && [
            // Non partial, just full ones
            ...prev.orderProductIds.filter(
              (opId) => !isProductPartialOffRent(opId) && !isProductPartialOffRentQuantity(opId),
            ),
            ...newSplitOrderProducts
              .filter(
                (nop) =>
                  nop.trackingType === ProductTrackingType.SerialUnits &&
                  prev.orderProductIds.find((opId) => {
                    return nop.assignedUnits
                      .map((u) => u.id)
                      .every((id) => partialOffRentUnitIds[opId.toString()]?.unitIds.includes(id));
                  }),
              )
              .map((nop) => nop.id),
            ...newSplitOrderProducts
              .filter((nop) => nop.trackingType === ProductTrackingType.Quantity)
              .map((nop) => nop.id),
          ];
          return (
            prev && {
              ...prev,
              orderProductIds: opIds ?? [],
              hourReadings: prev.hourReadings.filter((hr) =>
                opIds!.includes(
                  updatedOrder.products.find((op) => op.assignedUnits.find((au) => au.id === hr.unitId))?.id ?? 0,
                ),
              ),
            }
          );
        });
        setFuelCharges((prev) => {
          return (prev || []).map((fc) => {
            const op = updatedOrder.products.find((op) => op.assignedUnits.find((au) => au.id === fc.unitId));
            return { ...fc, orderProductId: op?.id ?? fc.orderProductId }; // update orderProductIds for units in case partial off rent
          });
        });

        setOrder(updatedOrder);
        onComplete();
      } else {
        onComplete();
      }
    } catch (err: any) {
      console.error(err);
      toast(err.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const onToggleOffRent = (op: OrderProduct) => {
    const isSelected = offRentRequest.offRentProducts.map((orp) => orp.productId).includes(op.id);
    setOffRentRequest((prev) => {
      return (
        prev && {
          ...prev,
          offRentProducts: [
            ...prev.offRentProducts.filter((orp) => orp.productId !== op.id),
            ...(isSelected ? [] : [{ productId: op.id, type: op.product.trackingType, quantity: op.quantity }]),
          ],
        }
      );
    });
    if (op.product.trackingType === ProductTrackingType.SerialUnits) {
      setPartialOffRentUnitIds((partialOffRentUnitIds) => ({
        ...partialOffRentUnitIds,
        [op.id]: {
          ...partialOffRentUnitIds[op.id],
          unitIds: partialOffRentUnitIds[op.id]?.unitIds ?? op.assignedUnits.map((au) => au.id),
        },
      }));
    }

    if (op.product.trackingType === ProductTrackingType.Quantity) {
      setPartialOffRentQuantities((prev) => {
        if (isSelected) {
          const { [op.id]: _, ...rest } = prev;
          return rest;
        } else {
          return {
            ...prev,
            [op.id]: {
              quantity: op.quantity,
              uniqueId: uuidv4(),
            },
          };
        }
      });
    }

    if (isSelected) {
      setInventoryReturnRequest((prev) => {
        return (
          prev && {
            ...prev,
            orderProductIds: prev.orderProductIds.filter((opId) => opId !== op.id),
          }
        );
      });
    }
  };

  const onToggleInventoryReturn = (op: OrderProduct) => {
    const isSelectedForReturn = inventoryReturnRequest?.orderProductIds.includes(op.id);
    setInventoryReturnRequest((prev) => {
      return (
        prev && {
          ...prev,
          orderProductIds: [
            ...prev.orderProductIds.filter((opId) => opId !== op.id),
            ...(isSelectedForReturn ? [] : [op.id]),
          ],
        }
      );
    });
  };

  return (
    <div className="confirm-off-rent">
      <div className="flex flex-column w-full gap-2">
        {showInventoryReturn && inventoryReturnRequest && locations?.length > 1 && (
          <InventoryReturnLocation
            locations={locations}
            order={order}
            returnToLocation={locations.find((l) => inventoryReturnRequest.returnToLocation === l.id)!}
            setReturnToLocation={(l) =>
              setInventoryReturnRequest((prev) => {
                return { ...prev!, returnToLocation: l.id };
              })
            }
            disabled={inventoryReturnRequest.orderProductIds.length === 0}
          />
        )}
        <div>
          <div className={`grid grid-cols-4 gap-4 text-[0.9rem] off-rent-table mb-8`}>
            {/* -------- */}
            <div className={showInventoryReturn ? 'col-span-2' : 'col-span-4'}>Products</div>
            {showInventoryReturn && (
              <div className="col-span-2">
                <label>Return Inventory</label>
              </div>
            )}
            {/* ---------- row break --------- */}
            {order.products
              .filter((op) => op.type === OrderType.Rental && op.status === OrderReturnStatus.OnRent)
              .map((op) => {
                const isSelected = offRentRequest.offRentProducts.map((orp) => orp.productId).includes(op.id);
                const isSelectedForReturn = inventoryReturnRequest?.orderProductIds.includes(op.id);

                return (
                  <>
                    {/* ---------- row break --------- */}
                    <div
                      key={`${op.id}-toggle`}
                      className={`flex items-center gap-2 ${!showInventoryReturn ? 'col-span-4' : 'col-span-2'}`}
                    >
                      <div>
                        <Toggle checked={isSelected} onClick={() => onToggleOffRent(op)} />
                      </div>
                      <div>{op.product.type === ProductType.Custom ? op.name : op.product.name}</div>
                    </div>
                    {showInventoryReturn && (
                      <div className="col-span-2" key={`${op.id}-return`}>
                        <Toggle checked={isSelectedForReturn} onClick={() => onToggleInventoryReturn(op)} />
                      </div>
                    )}

                    {/* ---------- row break --------- */}
                    {isSelected && op.product.trackingType === ProductTrackingType.Quantity && (
                      <QuantityOffRentInput
                        orderProduct={op}
                        setOffRentRequest={setOffRentRequest}
                        offRentRequest={offRentRequest}
                        setPartialOffRentQuantities={setPartialOffRentQuantities}
                      />
                    )}
                    {isSelected && op.product.trackingType === ProductTrackingType.SerialUnits && (
                      <>
                        <div className={`ml-8 col-span-2`}>Unit</div>
                        <div className="col-span-1">
                          {isSelectedForReturn &&
                            inventoryReturnRequest &&
                            op.product.trackingType === ProductTrackingType.SerialUnits &&
                            op.product.allowHourTracking && <label>Hours In</label>}
                        </div>

                        <div className="col-span-1">
                          {isSelectedForReturn && inventoryReturnRequest && op.product.fuelChargeProductId && (
                            <div className="flex-1">
                              Fuel ({(configs['CONF_FUEL_UNIT']?.value ?? 'Gallons') as 'Gallons' | 'Liters'})
                            </div>
                          )}
                        </div>

                        {/* ---------- row break --------- */}

                        {op.assignedUnits.flatMap((au) => {
                          const isUnitChecked = (partialOffRentUnitIds[op.id]?.unitIds ?? []).includes(au.id);
                          return (
                            <>
                              <div className={`flex gap-3 items-center ml-8 col-span-2`}>
                                <Checkbox
                                  isChecked={isUnitChecked}
                                  onChange={(v) =>
                                    setPartialOffRentUnitIds({
                                      ...partialOffRentUnitIds,
                                      [op.id]: {
                                        ...partialOffRentUnitIds[op.id],
                                        unitIds: v
                                          ? [...(partialOffRentUnitIds[op.id]?.unitIds ?? []), au.id]
                                          : (partialOffRentUnitIds[op.id]?.unitIds ?? []).filter((id) => id !== au.id),
                                      },
                                    })
                                  }
                                />
                                <div>{unitTrackingType === 'serialNumber' ? au.serialNumber : au.unitNumber}</div>
                              </div>

                              <div className="col-span-1">
                                {isSelectedForReturn && inventoryReturnRequest && isUnitChecked && (
                                  <InventoryReturnHourInput
                                    readingsDisabled={false}
                                    key={au.id}
                                    unitTrackingType={unitTrackingType}
                                    showUnit={false}
                                    unit={au}
                                    orderProduct={op}
                                    hourReading={
                                      inventoryReturnRequest.hourReadings.find((hr) => hr.unitId === au.id)?.hourReading
                                    }
                                    setHourReading={(hr) =>
                                      setInventoryReturnRequest((r) => {
                                        return r
                                          ? {
                                              ...r,
                                              hourReadings: [
                                                ...r.hourReadings.filter((hr) => hr.unitId !== au.id),
                                                { unitId: au.id, hourReading: hr },
                                              ],
                                            }
                                          : undefined;
                                      })
                                    }
                                    overage={hourOverages?.find(
                                      (overage) => overage.orderProductId === op.id && overage.unitId === au.id,
                                    )}
                                    overageOverride={hourOverageOverrides?.find(
                                      (overage) => overage.orderProductId === op.id && overage.unitId === au.id,
                                    )}
                                    setOverageOverride={(value) => {
                                      setHourOverageOverrides((existing) => {
                                        return [
                                          ...(existing || []).filter(
                                            (ov) => ov.orderProductId !== op.id && ov.unitId !== au.id,
                                          ),
                                          value,
                                        ];
                                      });
                                    }}
                                  />
                                )}
                              </div>

                              <div className="flex col-span-1 gap-4 items-baseline">
                                {isSelectedForReturn &&
                                  inventoryReturnRequest &&
                                  isUnitChecked &&
                                  op.product.fuelChargeProductId && (
                                    <InventoryReturnFuelChargeInput
                                      orderProduct={op}
                                      fuelAmount={fuelCharges.find((fc) => fc.unitId === au.id)?.amount}
                                      setFuelAmount={(fuelAmount) =>
                                        setFuelCharges((r) => {
                                          const existing = r?.find(
                                            (fc) => fc.orderProductId === op.id && fc.unitId === au.id,
                                          );

                                          return [
                                            ...(r || []).filter(
                                              (fc) =>
                                                fc.orderProductId !== op.id ||
                                                (fc.orderProductId === op.id && fc.unitId !== au.id),
                                            ),
                                            {
                                              ...existing,
                                              orderProductId: op.id,
                                              productId: op.product.fuelChargeProductId,
                                              unitId: au.id,
                                              amount: fuelAmount,
                                            },
                                          ];
                                        })
                                      }
                                    />
                                  )}
                              </div>
                            </>
                          );
                        })}
                      </>
                    )}
                  </>
                );
              })}
          </div>
        </div>
      </div>
      <div className="flex justify-between gap-3 w-full">
        <Button className="button button-secondary w-full" onClick={() => onCancel()}>
          Cancel
        </Button>
        <Button className="button button-yellow w-full" disabled={!isValid || isLoading} onClick={() => next()}>
          Next
        </Button>
      </div>
    </div>
  );
};

function orderToCalculateFinancialSummaryRequest(
  order: Order,
  customPrices: RentalProductPrice[],
  partialOffRentUnitIds: Dictionary<PartialOffRentUnits>,
  isProductPartialOffRentUnits: (opId: number) => boolean,
  partialOffRentQuantities: Dictionary<PartialOffRentQuantities>,
  isProductPartialOffRentQuantity: (opId: number) => boolean,
): CalculateFinancialSummaryRequest {
  const getNewQuantity = (op: OrderProduct) => {
    if (isProductPartialOffRentUnits(op.id)) {
      return op.quantity - (partialOffRentUnitIds[op.id]?.unitIds.length || 0);
    } else if (isProductPartialOffRentQuantity(op.id)) {
      return op.quantity - partialOffRentQuantities[op.id].quantity;
    } else {
      return op.quantity;
    }
  };

  return {
    customerId: order.user.id,
    locationId: order.locationId,
    deliveryType: order.products[0].shippingType,
    customDelivery:
      order.shippingUpdatedAt && order.shippingCharged !== undefined ? order.shippingCharged * 100 : undefined,
    customPickup: order.pickupUpdatedAt && order.pickupCharged !== undefined ? order.pickupCharged * 100 : undefined,
    customTax: order.taxUpdatedAt && order.taxCharged ? order.taxCharged * 100 : undefined,
    customTaxPercentage: order.taxPercentageUpdatedAt && order.taxPercentage,
    customDamageWaiver:
      order.damageWaiverUpdatedAt && order.damageWaiver !== undefined ? order.damageWaiver * 100 : undefined,
    customFees: order.additionalFees
      .filter((af) => af.updatedAt)
      .filter((fee) => fee.updatedAt && fee.updatedAt.toISO() !== null)
      .map((fee) => ({
        feeId: fee.feeId,
        amount: (fee.feeAppliedValue || 0) * 100,
      })),
    customProductFees: order.products.flatMap((product) =>
      product.additionalFees
        .filter((fee) => fee.updatedAt && fee.updatedAt.toISO() !== null)
        .map((fee) => ({
          feeId: fee.attributeId,
          productId: product.product.id,
          productLineItemId: `${product.id}`,
          amount: (fee.feeAppliedValue || 0) * 100,
        })),
    ),
    couponCode: order.discountCoupon,
    additionalDiscount:
      order.additionalDiscount && order.additionalDiscount > 0 ? order.additionalDiscount * 100 : undefined,
    billingAddressId: order.billingAddress?.id,
    deliveryAddressId: order.shippingAddress?.id,
    paymentMethod: order.paymentMethodType,
    isCycleBilled: order.isCycleBilled,
    cycleLength: order.cycleBillingTerms?.cycleLength,
    items: [
      // Will be the items NOT off renting
      ...order.products.map((op) => ({
        ...op,
        quantity: getNewQuantity(op),
      })),
      // Will be new items that we ARE off renting
      ...partialOffRentProductsToOrderProducts(order, partialOffRentUnitIds, isProductPartialOffRentUnits),
      ...partialOffRentProductsToOrderProductsQuantity(
        order,
        partialOffRentQuantities,
        isProductPartialOffRentQuantity,
        customPrices,
      ),
    ].map((op) => {
      return orderProductToOrderInputItem(
        {
          ...op,
          id: `${op.id ?? ''}`,
          rentalEndDate:
            order.isCycleBilled && op.type === OrderType.Rental
              ? op.rentalStartDate.plus(order.cycleBillingTerms!.cycleLength)
              : op.rentalEndDate,
        },
        customPrices.filter(
          (cp) => cp.productId === op.product.id && (cp.orderProductId == op.id || !cp.orderProductId),
        ),
      );
    }),
    taxRateId: order.taxCode,
    documentType: OrderDocumentType.Order,
  };
}

function partialOffRentProductsToOrderProducts(
  order: Order,
  partialOffRentUnitIds: Dictionary<PartialOffRentUnits>,
  isProductPartialOffRent: (opId: number) => boolean,
): (Omit<OrderProduct, 'id'> & { id: string })[] {
  return Object.keys(partialOffRentUnitIds).flatMap((opId) => {
    if (!isProductPartialOffRent(parseInt(opId))) return [];
    const op = order.products.find((op) => op.id === parseInt(opId))!;
    return [
      {
        ...op,
        id: partialOffRentUnitIds[opId].uniqueId!,
        uniqueId: partialOffRentUnitIds[opId].uniqueId!,
        status: OrderReturnStatus.OnRent,
        quantity: partialOffRentUnitIds[opId].unitIds.length,
        assignedUnits: op.assignedUnits.filter((au) => partialOffRentUnitIds[opId].unitIds.includes(au.id)),
      },
    ];
  });
}

function partialOffRentProductsToOrderProductsQuantity(
  order: Order,
  partialOffRentQuantities: Dictionary<PartialOffRentQuantities>,
  isProductPartialOffRentQuantity: (opId: number) => boolean,
  customPrices: RentalProductPrice[],
): (Omit<OrderProduct, 'id'> & { id: string })[] {
  return Object.keys(partialOffRentQuantities).flatMap((opId) => {
    if (!isProductPartialOffRentQuantity(parseInt(opId))) return [];
    const op = order.products.find((op) => op.id === parseInt(opId))!;

    let prices: RentalProductPrice[] = [];
    if (op.product.type === ProductType.Custom) {
      const customPricesArr = customPrices.filter(
        (cp) => cp.productId === op.product.id && cp.orderProductId === op.id,
      );
      prices = customPricesArr;
    }

    return [
      {
        ...op,
        id: partialOffRentQuantities[opId].uniqueId!,
        uniqueId: partialOffRentQuantities[opId].uniqueId!,
        status: OrderReturnStatus.OnRent,
        quantity: partialOffRentQuantities[opId].quantity,
        customOrderRentalRates: prices,
      },
    ];
  });
}

function toPartialOffRentRequest(
  order: Order,
  customPrices: RentalProductPrice[],
  summary: FinancialSummary,
  offRentRequest: OffRentRequest, // will only have order product ids of products with all units being off rented
  currency: Dinero.Currency,
  partialOffRentUnitIds: Dictionary<PartialOffRentUnits>,
  isProductPartialOffRent: (opId: number) => boolean,
  partialOffRentQuantities: Dictionary<PartialOffRentQuantities>,
  isProductPartialOffRentQuantity: (opId: number) => boolean,
): SplitOrderProductsUpdateRequest {
  const cartTotal = summary.lineItems.reduce(
    (acc, line) => (line.type === 'Product' ? acc.add(line.amount) : acc),
    Dinero({ amount: 0, currency }),
  );
  const orderDelivery = summary?.lineItems.find((line) => line.type === 'Delivery') as DeliveryLineItem;
  const damageWaiver = summary?.lineItems.find((line) => line.type === 'DamageWaiver') as DamageWaiverLineItem;
  const delivery = orderDelivery
    ? orderDelivery.amount
    : summary?.lineItems.reduce(
        (acc, line) => (line.type === 'ProductDelivery' ? acc.add(line.amount) : acc),
        Dinero({ amount: 0, currency }),
      );
  const orderPickup = summary?.lineItems.find((line) => line.type === 'Pickup') as PickupLineItem;
  const pickup = orderPickup
    ? orderPickup.amount
    : summary?.lineItems.reduce(
        (acc, line) => (line.type === 'ProductPickup' ? acc.add(line.amount) : acc),
        Dinero({ amount: 0, currency }),
      );
  const tax = summary?.lineItems.find((line) => line.type === 'Tax') as TaxLineItem;
  const couponCode = order.user.tagPricing?.name || order.discountCoupon;
  const couponDiscount =
    summary?.lineItems.find((line) => line.type === 'TagPricing' || line.type === 'Coupon')?.amount ||
    Dinero({ amount: 0, currency });
  const locationFees = (summary?.lineItems.filter((line) => line.type === 'Fee') || []) as FeeLineItem[];
  const existingProductFees = order.products.flatMap((p) => p.additionalFees);
  const discount =
    summary?.lineItems.find((line) => line.type === 'AdditionalDiscount')?.amount ?? Dinero({ amount: 0, currency });

  const newProduct = (
    op: OrderProduct,
  ): Omit<OrderProduct, 'id'> & {
    id: string;
  } => {
    const stringOp = {
      ...op,
      id: op.id.toString(),
    };

    if (op.trackingType === ProductTrackingType.SerialUnits) {
      return {
        ...stringOp,
        assignedUnits:
          op.product.trackingType === ProductTrackingType.SerialUnits
            ? op.assignedUnits.filter(
                (au) =>
                  !isProductPartialOffRent(op.id) ||
                  (isProductPartialOffRent(op.id) && !(partialOffRentUnitIds[op.id]?.unitIds ?? []).includes(au.id)),
              )
            : [],
      };
    } else if (op.trackingType === ProductTrackingType.Quantity) {
      return isProductPartialOffRentQuantity(op.id)
        ? {
            ...stringOp,
            quantity: op.quantity - partialOffRentQuantities[op.id].quantity,
          }
        : stringOp;
    } else {
      return stringOp;
    }
  };

  return {
    locationId: order.locationId,
    billingAddressId: order.billingAddress?.id,
    deliveryAddressId: order.shippingAddress?.id,
    deliveryType: order.products[0].shippingType,
    currency: summary.total.getCurrency(),
    cartTotal: cartTotal.getAmount(),
    damageWaiver: damageWaiver
      ? {
          amount: damageWaiver.amount.getAmount(),
          hasCustomAmount: damageWaiver.hasCustomAmount,
        }
      : undefined,
    delivery: delivery
      ? {
          amount: delivery?.getAmount() || 0,
          hasCustomAmount: orderDelivery?.hasCustomAmount || false,
        }
      : undefined,
    pickup: pickup
      ? {
          amount: pickup?.getAmount() || 0,
          hasCustomAmount: orderPickup?.hasCustomAmount || false,
        }
      : undefined,
    tax: {
      taxCode: tax.taxCodeId,
      amount: tax.amount.getAmount(),
      hasCustomAmount: tax.hasCustomAmount,
      hasCustomPercentage: tax.hasCustomPercentage,
      percentage: tax.percentage,
    },
    coupon: couponCode
      ? {
          code: couponCode,
          amount: couponDiscount.multiply(-1).getAmount(),
          isVip: false,
        }
      : undefined,
    additionalFees: locationFees.map((fee) => {
      const orderFee = order.additionalFees.find((f) => f.feeId === fee.feeId);

      return {
        orderFeeId: orderFee?.id,
        feeId: fee.feeId,
        amount: fee.amount.getAmount(),
        hasCustomAmount: fee.hasCustomAmount,
      };
    }),
    additionalDiscount: !discount.isZero() ? discount.multiply(-1).getAmount() : undefined,
    total: summary.total.getAmount(),
    googleCalculation: summary.googleCalculation,
    securityDeposit:
      order.rentalSecurity !== 0
        ? {
            amount: round(order.rentalSecurity * 100),
            percentage: order.rentalSecurityPercentage,
          }
        : undefined,
    documentType: OrderDocumentType.Order,
    products: (
      [
        ...order.products.map((op) => newProduct(op)),
        ...partialOffRentProductsToOrderProducts(order, partialOffRentUnitIds, isProductPartialOffRent),
        ...partialOffRentProductsToOrderProductsQuantity(
          order,
          partialOffRentQuantities,
          isProductPartialOffRentQuantity,
          customPrices,
        ),
      ] as (Omit<OrderProduct, 'id'> & { id: string })[]
    ).map((op) => {
      const productLineItem = summary.lineItems.find(
        (line) => (line.type === 'Product' || line.type === 'CustomProduct') && line.id === op.id?.toString(),
      )! as ProductLineItem;
      return {
        uniqueId: op.id,
        productId: op.product.id, // productLineItem.productId, TODO: unsure why this was line item?
        type: productLineItem.transactionType,
        amount: productLineItem.unitPrice.getAmount(),
        hasCustomAmount: productLineItem.hasCustomAmount,
        isCustom: op.product.type === (ProductType.Custom as const),
        taxable: !op.isTaxFree,
        name: op.product.type === ProductType.Custom ? op.name : undefined,
        description: op.product.type === ProductType.Custom ? op.description : undefined,
        generalLedgerAccountId: op.generalLedgerAccountId,
        deliveryDriverId: op.deliveryDriverId,
        pickupDriverId: op.pickupDriverId,
        taxCategoryId: op.taxCategoryId,
        equipmentOwner: op.equipmentOwner,
        deliveryType: toShippingType(op.shippingTypeId),
        pickup:
          summary.lineItems
            .find((line) => line.type === 'ProductPickup' && line.productLineItemId === productLineItem.id)
            ?.amount?.getAmount() || 0,
        delivery:
          summary.lineItems
            .find((line) => line.type === 'ProductDelivery' && line.productLineItemId === productLineItem.id)
            ?.amount?.getAmount() || 0,
        securityDeposit: summary.lineItems
          .find((line) => line.type === 'ProductSecurityDeposit' && line.productLineItemId === productLineItem.id)
          ?.amount?.getAmount(),
        quantity: op.trackingType === ProductTrackingType.Quantity ? op.quantity : productLineItem.quantity, // productLineItem.quantity, unsure why this was line item
        additionalFees: summary.lineItems
          .filter((line) => line.type === 'ProductFee' && line.productLineItemId === productLineItem.id)
          .map((line) => {
            const fee = line as ProductFeeLineItem;
            const orderProductFee = existingProductFees.find(
              (f) => f.attributeId === fee.productFeeId && `${f.orderProductId}` === productLineItem.id,
            );

            return {
              orderFeeId: orderProductFee?.id,
              feeId: fee.productFeeId,
              amount: fee.amount.getAmount(),
              hasCustomAmount: fee.hasCustomAmount,
            };
          }),
        discount: op.discountPercentage,
        start: op.rentalStartDate,
        end: op.rentalEndDate,
        customOrderRentalRates:
          op.product.type === ProductType.Custom
            ? customPrices.filter((cp) => cp.productId === op.product.id)
            : customPrices.filter(
                (cp) => cp.productId === op.product.id && (cp.orderProductId === parseInt(op.id) || !cp.orderProductId),
              ),
        taxCode: order.taxCode,
        options: op.options.map((o) => ({
          orderProductId: op.id,
          id: o.productOptionId,
          valueId: o.productOptionValueId,
          name: o.name,
          value: o.value,
        })),
        services: [],
        assignedUnits: op.assignedUnits.map((iu) => iu.id),
        // partial off rent specific stuff
        status: op.status,
        actualRentalStartDate: op.actualRentalStartDate,
      };
    }),
  };
}
